
import React, { useEffect } from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { About } from './components/About';
import { Services } from './components/Services';
import { Recruitment } from './components/Recruitment';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { FloatingContactButton } from '../../components/feature/FloatingContactButton';

export default function Home() {
  useEffect(() => {
    // Schema.org JSON-LD for Organization
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "訪問介護のんな福島",
      "legalName": "株式会社ピュアステップ",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": "https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/73be6e95a9cc4159c8afb514f102f80a.png",
      "image": "https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/73be6e95a9cc4159c8afb514f102f80a.png",
      "description": "福島市野田町の訪問介護のんな福島。身体介護・生活援助・医療連携・24時間対応で利用者様の在宅生活をサポート。",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "野田町7-13-50-2階",
        "addressLocality": "福島市",
        "addressRegion": "福島県",
        "postalCode": "960-8055",
        "addressCountry": "JP"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+81-24-563-7717",
        "contactType": "customer service",
        "availableLanguage": "Japanese",
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00"
        }
      },
      "sameAs": [],
      "foundingDate": "2023",
      "numberOfEmployees": "10-20",
      "industry": "Healthcare Services",
      "serviceArea": {
        "@type": "Place",
        "name": "福島市"
      }
    };

    // Schema.org JSON-LD for Home Care Service
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "訪問介護のんな福島",
      "image": "https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/73be6e95a9cc4159c8afb514f102f80a.png",
      "description": "福島市野田町の訪問介護サービス。身体介護・生活援助・医療連携・24時間対応で利用者様の在宅生活をサポート。",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "野田町7-13-50-2階",
        "addressLocality": "福島市",
        "addressRegion": "福島県",
        "postalCode": "960-8055",
        "addressCountry": "JP"
      },
      "telephone": "+81-24-563-7717",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "priceRange": "$$",
      "openingHours": "Mo-Fr 08:00-18:00",
      "medicalSpecialty": "Home Care",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "訪問介護サービス",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "身体介護",
              "description": "入浴・排泄・食事介助等の身体介護サービス"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "生活援助",
              "description": "掃除・洗濯・買い物・調理等の生活援助サービス"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "医療連携",
              "description": "医療機関との連携によるケアサービス"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "24時間対応",
              "description": "緊急時の24時間対応サービス"
            }
          }
        ]
      }
    };

    // Schema.org JSON-LD for Job Postings
    const jobPostingSchema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "サービス提供責任者・介護職員募集",
      "description": "福島市の訪問介護のんな福島でサービス提供責任者・介護職員を募集。未経験者歓迎、充実した研修制度でサポート。",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "訪問介護のんな福島",
        "sameAs": import.meta.env.VITE_SITE_URL || "https://example.com"
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "野田町7-13-50-2階",
          "addressLocality": "福島市",
          "addressRegion": "福島県",
          "postalCode": "960-8055",
          "addressCountry": "JP"
        }
      },
      "employmentType": ["FULL_TIME", "PART_TIME"],
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "JPY",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": 204000,
          "maxValue": 295000,
          "unitText": "MONTH"
        }
      },
      "datePosted": "2023-01-01",
      "validThrough": "2024-12-31",
      "applicantLocationRequirements": {
        "@type": "Country",
        "name": "Japan"
      },
      "jobBenefits": "社会保険完備、賞与あり、昇給あり、資格取得支援制度、退職金制度、マイカー通勤可能"
    };

    // Add schemas to head
    const addSchema = (schema: object, id: string) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(organizationSchema, 'organization-schema');
    addSchema(serviceSchema, 'service-schema');
    addSchema(jobPostingSchema, 'job-schema');

    return () => {
      // Cleanup schemas on unmount
      document.getElementById('organization-schema')?.remove();
      document.getElementById('service-schema')?.remove();
      document.getElementById('job-schema')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Hero />
      <About />
      <Services />
      <Recruitment />
      <Contact />
      <Footer />
      <FloatingContactButton />
    </div>
  );
}
